import { decryptedMessagesDB } from './decrypted-messages-cache';

/**
 * NIP-07 window.nostr interface
 * This is the interface provided by Nostr browser extensions like nos2x, Alby, etc.
 */
interface Nip07Nostr {
  getPublicKey(): Promise<string>;
  signEvent(event: unknown): Promise<unknown>;
  nip04?: {
    encrypt(pubkey: string, plaintext: string): Promise<string>;
    decrypt(pubkey: string, ciphertext: string): Promise<string>;
  };
  nip44?: {
    encrypt(pubkey: string, plaintext: string): Promise<string>;
    decrypt(pubkey: string, ciphertext: string): Promise<string>;
  };
}

declare global {
  interface Window {
    nostr?: Nip07Nostr;
  }
}

/**
 * Check if a NIP-07 compatible Nostr extension is available
 */
export function isNostrExtensionAvailable(): boolean {
  return typeof window !== 'undefined' && !!window.nostr;
}

/**
 * Check if NIP-44 decryption is supported by the extension
 */
export function isNip44Supported(): boolean {
  return isNostrExtensionAvailable() && !!window.nostr?.nip44?.decrypt;
}

/**
 * Error thrown when Nostr extension is not available
 */
export class NostrExtensionError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'NostrExtensionError';
  }
}

/**
 * Error thrown when decryption fails
 */
export class DecryptionError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'DecryptionError';
  }
}

/**
 * Decrypt a Nostr-encrypted message using NIP-44
 *
 * @param encryptedData The encrypted ciphertext from the server
 * @param senderPubkey The ephemeral pubkey used by the server for encryption (hex format)
 * @returns The decrypted message data as a parsed JSON object
 */
async function decryptNostrMessage(
  encryptedData: string,
  senderPubkey: string
): Promise<Record<string, unknown>> {
  if (!isNostrExtensionAvailable()) {
    throw new NostrExtensionError(
      'Nostr extension not found. Please install a NIP-07 compatible extension like nos2x, Alby, or Flamingo.'
    );
  }

  if (!isNip44Supported()) {
    throw new NostrExtensionError(
      'Your Nostr extension does not support NIP-44 decryption. Please update your extension or use one that supports NIP-44.'
    );
  }

  try {
    const decryptedText = await window.nostr!.nip44!.decrypt(senderPubkey, encryptedData);
    const decryptedData = JSON.parse(decryptedText);
    return decryptedData as Record<string, unknown>;
  } catch (error) {
    if (error instanceof SyntaxError) {
      throw new DecryptionError('Decrypted data is not valid JSON');
    }
    if (error instanceof Error) {
      // User might have rejected the decryption request
      if (error.message.includes('rejected') || error.message.includes('denied')) {
        throw new DecryptionError('Decryption request was rejected by user');
      }
      throw new DecryptionError(`Decryption failed: ${error.message}`);
    }
    throw new DecryptionError('Unknown decryption error occurred');
  }
}

/**
 * Decrypt a message and cache the result
 *
 * @param messageId The message ID for caching
 * @param encryptedData The encrypted ciphertext
 * @param senderPubkey The ephemeral pubkey used for encryption
 * @returns The decrypted message data
 */
export async function decryptAndCacheMessage(
  messageId: string,
  encryptedData: string,
  senderPubkey: string
): Promise<Record<string, unknown>> {
  // Check cache first
  const cached = await decryptedMessagesDB.getDecryptedMessage(messageId);
  if (cached) {
    return cached.decryptedData;
  }

  // Decrypt the message
  const decryptedData = await decryptNostrMessage(encryptedData, senderPubkey);

  // Cache the result
  await decryptedMessagesDB.cacheDecryptedMessage(messageId, decryptedData);

  return decryptedData;
}

/**
 * Get cached decrypted message if available
 */
export async function getCachedDecryptedMessage(
  messageId: string
): Promise<Record<string, unknown> | null> {
  const cached = await decryptedMessagesDB.getDecryptedMessage(messageId);
  return cached?.decryptedData ?? null;
}

/**
 * Clear the decrypted messages cache
 * Should be called on logout
 */
export async function clearDecryptedMessagesCache(): Promise<void> {
  await decryptedMessagesDB.clearAll();
}
